import { useCallback, useRef, useState } from 'react';
import useCreateMonduHandoff, { isSafeHandoffRedirectUrl } from '@/enterprise/data/useCreateMonduHandoff';
import { useReporting } from '@/reporting';
import { isImpersonationSession } from '@/utils/token';

/**
 * How long to let the redirect track event land before leaving for Mondu. `apiClient` sets no
 * timeout, so the wait is capped: a slow analytics call must not strand the supplier on a spinner.
 */
const TRACK_FLUSH_TIMEOUT_MS = 500;

/**
 * Which consent path the supplier is on, reported on every ENF handoff and consent-modal event.
 * `prior-consent` suppliers can still reach the modal — a failed skip drops them into it — so this
 * travels with them rather than being assumed from where the event fired.
 */
export type ConsentPath = 'modal' | 'prior-consent';

interface RunHandoffOptions {
  /**
   * `modal` when the supplier is consenting right now, `prior-consent` when they already
   * acknowledged the current terms and already have a Mondu supplier.
   */
  consentPath: ConsentPath;
}

interface UseMonduHandoffRedirectResult {
  /** Creates the handoff and navigates to Mondu. Resolves `false` when nothing was navigated. */
  runHandoff: (options: RunHandoffOptions) => Promise<boolean>;
  isBusy: boolean;
  hasError: boolean;
  /** Clears a previous failure so the next attempt starts clean. */
  clearError: () => void;
}

/**
 * The single path from Extended Network Finance to Mondu: create the handoff, verify the redirect
 * URL, give the analytics event a bounded head start, then navigate the current tab.
 *
 * Both callers share this hook — the disclosure modal's Proceed button and the page's skip path for
 * suppliers who already consented — so there is only one place where a consent-recording POST and a
 * redirect can happen.
 */
const useMonduHandoffRedirect = (): UseMonduHandoffRedirectResult => {
  const { trackEnterpriseEvent } = useReporting();
  const { mutateAsync: createMonduHandoff, isPending, reset } = useCreateMonduHandoff();
  const [hasError, setHasError] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // `isBusy` only turns a click away once React has re-rendered; two clicks in the same frame would
  // both see it false, so the in-flight flag also lives in a ref that updates immediately.
  const isRunningRef = useRef(false);
  const isBusy = isPending || isRedirecting;

  const clearError = useCallback((): void => {
    setHasError(false);
    reset();
  }, [reset]);

  const runHandoff = useCallback(
    async ({ consentPath }: RunHandoffOptions): Promise<boolean> => {
      // This hook makes the only handoff POST in the app, and that POST records the Mondu
      // data-sharing consent, so the impersonation guard belongs here as well as on the modal's
      // Proceed button.
      if (isImpersonationSession() || isBusy || isRunningRef.current) {
        return false;
      }

      const failHandoff = (reason: 'request' | 'unsafe-url'): void => {
        isRunningRef.current = false;
        setHasError(true);
        trackEnterpriseEvent('extended-network-marketing::handoff::failed', { reason, consentPath });
      };

      isRunningRef.current = true;
      setHasError(false);
      trackEnterpriseEvent('extended-network-marketing::handoff::started', { consentPath });

      try {
        const { redirectUrl } = await createMonduHandoff();

        if (!isSafeHandoffRedirectUrl(redirectUrl)) {
          failHandoff('unsafe-url');
          return false;
        }

        // Navigating the current tab keeps popup blockers out of the flow entirely — they only police
        // window.open, and this handoff opens after an await, so it never looks like a fresh gesture.
        // Hold the loading state instead of clearing it: unload is not instant, and clearing first
        // would flash the marketing page and re-enable the button mid-departure.
        setIsRedirecting(true);

        // Unlike a new tab, this navigation unloads the page and aborts the analytics request, so give
        // the event a bounded head start instead of dropping it.
        await Promise.race([
          trackEnterpriseEvent('extended-network-marketing::handoff::redirected', { consentPath }),
          new Promise((resolve) => setTimeout(resolve, TRACK_FLUSH_TIMEOUT_MS)),
        ]);

        window.location.assign(redirectUrl);

        return true;
      } catch {
        // Also covers a throwing assign, which would otherwise strand the caller in its loading state.
        setIsRedirecting(false);
        failHandoff('request');

        return false;
      }
    },
    [createMonduHandoff, isBusy, trackEnterpriseEvent]
  );

  return { runHandoff, isBusy, hasError, clearError };
};

export default useMonduHandoffRedirect;
